/// TaskList: Shows active user tasks with selection, claim, and reject actions.
/// Each task row displays the task name, formKey, assignee, and action buttons.

import { User } from "lucide-react";
import { useEngineStore } from "@/stores/engineStore";
import { useUserTasks } from "@/hooks/useUserTasks";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TaskList() {
  const userTasks = useEngineStore((s) => s.userTasks);
  const activeTaskJobKey = useEngineStore((s) => s.activeTaskJobKey);
  const setActiveTask = useEngineStore((s) => s.setActiveTask);
  const { failTask } = useUserTasks();

  return (
    <div className="flex flex-col">
      {userTasks.map((task) => {
        const isActive = activeTaskJobKey === task.jobKey;
        const timeSince = formatTimeSince(task.activatedAt);

        return (
          <div
            key={task.jobKey}
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-zinc-800 border-b border-zinc-800 ${
              isActive ? "bg-zinc-800 border-l-2 border-l-blue-500" : ""
            }`}
            onClick={() => setActiveTask(task.jobKey)}
          >
            <User size={14} className="text-blue-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-zinc-200 truncate">{task.taskName}</div>
              <div className="text-[10px] text-zinc-500">
                {task.formKey} | {task.assignee ?? "Unassigned"} | {timeSince}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveTask(task.jobKey);
                }}
                className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-500 transition-colors"
              >
                Open
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  failTask(task.jobKey, "Rejected by user");
                }}
                className="text-[10px] bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded hover:bg-zinc-600 transition-colors"
              >
                Reject
              </button>
            </div>
          </div>
        );
      })}
      {userTasks.length === 0 && (
        <div className="flex items-center justify-center py-8 text-xs text-zinc-500">
          No active user tasks
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimeSince(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
